import { Injectable, computed, inject, signal } from '@angular/core';

import { AuthService } from './auth.service';
import { EngagementHubService } from './engagement-hub.service';
import { VideoHubService } from './video-hub.service';
import { resolveMyChatId } from '../chat-identity.util';
import {
  privateRoomName,
  type CallMediaType,
  type LiveKitCredentials
} from '../models/video.models';

/**
 * Lifecycle phase of the single active 1:1 call.
 *
 *  - `idle`       — no call in progress.
 *  - `outgoing`   — we rang a peer and are waiting for them to answer.
 *  - `incoming`   — a peer is ringing us; the answer sheet is shown.
 *  - `connecting` — the call was answered; we're fetching LiveKit creds.
 *  - `connected`  — credentials in hand; the LiveKit room is mounted.
 */
export type CallStatus = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'connected';

export interface CallState {
  status: CallStatus;
  /** Deterministic LiveKit room name shared by both parties. */
  roomName: string | null;
  /** Chat id of the other party. */
  peerId: string | null;
  /** Display name of the other party for the call UI. */
  peerName: string;
  callType: CallMediaType;
  /** LiveKit credentials — present only once `status === 'connected'`. */
  credentials: LiveKitCredentials | null;
  /** Transient error surfaced in the call UI (e.g. peer busy). */
  error: string | null;
}

const IDLE: CallState = {
  status: 'idle',
  roomName: null,
  peerId: null,
  peerName: '',
  callType: 'video',
  credentials: null,
  error: null
};

/**
 * Orchestrates a single 1:1 networking video/audio call — the mobile
 * counterpart of the web venue's call flow (room.component + private-call
 * modal). It glues two transports together:
 *
 *  - `EngagementHubService` carries the *signalling* (ring / accept /
 *    decline / cancel / end) over `/hubs/engagement`.
 *  - `VideoHubService` mints the LiveKit *credentials* for the negotiated
 *    private room over `/hubs/video`.
 *
 * The whole platform supports one active call at a time, so the state is a
 * single signal. `CallOverlayComponent` renders off it and the LiveKit room
 * mounts once `status === 'connected'`.
 */
@Injectable({ providedIn: 'root' })
export class CallService {
  private readonly auth = inject(AuthService);
  private readonly engagement = inject(EngagementHubService);
  private readonly videoHub = inject(VideoHubService);

  private readonly _state = signal<CallState>(IDLE);
  readonly state = this._state.asReadonly();

  /** True whenever any call UI should be on screen. */
  readonly isActive = computed(() => this._state().status !== 'idle');

  constructor() {
    // A peer is ringing us. Ignore if we're already mid-call (one call at a
    // time) — the caller will see no answer and can cancel.
    this.engagement.incomingCall$.subscribe((notice) => {
      if (this._state().status !== 'idle') return;
      this._state.set({
        status: 'incoming',
        roomName: notice.roomName,
        peerId: notice.callerId,
        peerName: notice.callerName,
        callType: notice.callType,
        credentials: null,
        error: null
      });
    });

    // The peer we rang answered — fetch creds and join the room.
    this.engagement.callAccepted$.subscribe((notice) => {
      const current = this._state();
      if (current.status !== 'outgoing' || current.roomName !== notice.roomName) return;
      void this.joinRoom(notice.roomName);
    });

    // Peer declined / cancelled / ended — tear the call down.
    this.engagement.callDeclined$.subscribe((n) => this.endIfRoom(n.roomName, 'Call declined.'));
    this.engagement.callCancelled$.subscribe((n) => this.endIfRoom(n.roomName, null));
    this.engagement.callEnded$.subscribe((n) => this.endIfRoom(n.roomName, null));
  }

  // ---------------------------------------------------------------------
  // Outgoing
  // ---------------------------------------------------------------------

  /**
   * Ring `peer` for a 1:1 call. No-op when a call is already in progress.
   * Surfaces a friendly error in the call state if the peer is busy or the
   * hub isn't reachable.
   */
  async startCall(peerId: string, peerName: string, callType: CallMediaType = 'video'): Promise<void> {
    if (this._state().status !== 'idle') return;

    const myId = resolveMyChatId(this.auth.getToken());
    if (!myId) {
      this._state.set({ ...IDLE, status: 'outgoing', peerName, error: 'Sign in again to start a call.' });
      return;
    }
    if (myId === peerId) return; // Can't call yourself.

    const roomName = privateRoomName(myId, peerId);
    this._state.set({
      status: 'outgoing',
      roomName,
      peerId,
      peerName,
      callType,
      credentials: null,
      error: null
    });

    try {
      await this.engagement.ensureConnected();
      await this.engagement.initiateCall(peerId, callType, roomName);
    } catch (err) {
      const message = err instanceof Error && /in a call/i.test(err.message)
        ? `${peerName} is already in a call.`
        : `Couldn't reach ${peerName}. Please try again.`;
      this._state.update((s) => ({ ...s, error: message }));
      // Leave the error on screen briefly, then reset.
      setTimeout(() => this.reset(), 2_500);
    }
  }

  // ---------------------------------------------------------------------
  // Incoming
  // ---------------------------------------------------------------------

  /** Accept the current incoming call and join the LiveKit room. */
  async accept(): Promise<void> {
    const current = this._state();
    if (current.status !== 'incoming' || !current.roomName || !current.peerId) return;
    this._state.update((s) => ({ ...s, status: 'connecting', error: null }));
    try {
      await this.engagement.acceptCall(current.roomName, current.peerId);
      await this.joinRoom(current.roomName);
    } catch {
      this._state.update((s) => ({ ...s, error: 'Failed to join the call.' }));
      setTimeout(() => this.reset(), 2_500);
    }
  }

  /** Decline the current incoming call. */
  async decline(): Promise<void> {
    const current = this._state();
    if (current.status === 'incoming' && current.roomName && current.peerId) {
      await this.engagement.declineCall(current.roomName, current.peerId);
    }
    this.reset();
  }

  // ---------------------------------------------------------------------
  // Teardown (works for outgoing / connecting / connected)
  // ---------------------------------------------------------------------

  /**
   * Hang up the active call: cancels an unanswered outgoing call or ends a
   * connected one, notifying the peer over the hub. Always resets to idle.
   */
  async hangUp(): Promise<void> {
    const { status, roomName } = this._state();
    if (roomName) {
      if (status === 'outgoing') {
        await this.engagement.cancelCall(roomName);
      } else if (status === 'connecting' || status === 'connected') {
        await this.engagement.endCall(roomName);
      }
    }
    this.reset();
  }

  /** Drop straight to idle without signalling (e.g. after the peer hung up). */
  reset(): void {
    this._state.set(IDLE);
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private async joinRoom(roomName: string): Promise<void> {
    this._state.update((s) => ({ ...s, status: 'connecting', error: null }));
    try {
      const credentials = await this.videoHub.joinPrivateCallRoom(roomName);
      // Bail if the call was torn down while we were fetching creds.
      if (this._state().roomName !== roomName) return;
      this._state.update((s) => ({ ...s, status: 'connected', credentials }));
    } catch {
      this._state.update((s) => ({ ...s, error: 'Could not connect the call.' }));
      const room = this._state().roomName;
      if (room) void this.engagement.endCall(room);
      setTimeout(() => this.reset(), 2_500);
    }
  }

  private endIfRoom(roomName: string, message: string | null): void {
    if (this._state().roomName !== roomName) return;
    if (message) {
      this._state.update((s) => ({ ...s, error: message }));
      setTimeout(() => this.reset(), 1_800);
    } else {
      this.reset();
    }
  }
}
